import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FileText,
  ArrowUp,
  ArrowDown,
  Search,
  Building2,
  User,
  Upload,
  Download,
  Folder,
  Plus,
  ArrowLeft,
  Trash2,
  UserCheck,
  X,
  Loader2,
  ChevronDown,
  CheckCircle,
} from "lucide-react";
import BulkDownloadModal from "../../modals/BulkDownloadModal";
import AdminUploadProcessedModal from "../../modals/AdminUploadProcessedModal";
import AdminFolderAssignModal from "../../modals/AdminFolderAssignModal";
import {
  adminGetFolders,
  adminCreateFolder,
  adminDeleteFolder,
  adminGetFolderDocuments,
  getAllDocumentsAdmin,
  deleteDocument,
  adminUpdateDocumentStatus,
} from "../../utils/Api";
import { fetchDocumentRawJsonString } from "../../utils/documentJson";
import { createZipBlob } from "../../utils/zip";

interface AdminFolder {
  id: string;
  name: string;
  created_at: string | null;
  doc_count: number;
  pending_count: number;
}

type DocStatus = "pending" | "ready" | "validated" | "delivered";

interface FolderDocument {
  id: string;
  name: string;
  created_at: string | null;
  page_count: number;
  pending_page_count?: number;
  status: DocStatus;
  delivered_to: string | null;
  marked_by: string | null;
  is_processing?: boolean;
}

interface AdminDocument {
  id: string;
  name: string;
  created_at: string | null;
  page_count: number;
  organisation_id: string | null;
  organisation_name: string | null;
  user_email: string | null;
  user_name: string | null;
}

type View = "folders" | "folder-detail" | "all-docs";

function AdminDocuments() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<View>("folders");

  // Folders list
  const [folders, setFolders] = useState<AdminFolder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [foldersError, setFoldersError] = useState("");

  // Create folder
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");

  // Delete folder
  const [deletingFolderId, setDeletingFolderId] = useState<string | null>(null);

  // Folder detail
  const PAGE_SIZE = 20;
  const [activeFolder, setActiveFolder] = useState<AdminFolder | null>(null);
  const [folderDocs, setFolderDocs] = useState<FolderDocument[]>([]);
  const [folderDocsLoading, setFolderDocsLoading] = useState(false);
  const [folderDocsLoadingMore, setFolderDocsLoadingMore] = useState(false);
  const [folderDocsTotal, setFolderDocsTotal] = useState(0);
  const [folderTotal, setFolderTotal] = useState(0);
  const [folderStatusCounts, setFolderStatusCounts] = useState({ pending: 0, ready: 0, validated: 0, delivered: 0 });
  const [loadedCount, setLoadedCount] = useState(PAGE_SIZE);
  const loadedCountRef = useRef(PAGE_SIZE);
  // Set during URL restore so the initial fetch loads the restored window
  // instead of resetting to PAGE_SIZE.
  const initialLoadCountRef = useRef<number | null>(null);
  const folderDocSearchRef = useRef("");
  const folderDocStatusFilterRef = useRef<"all" | DocStatus>("all");
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Download all folder documents' raw JSON as a ZIP
  const [isDownloadingJson, setIsDownloadingJson] = useState(false);
  const [jsonDownloadProgress, setJsonDownloadProgress] = useState({ done: 0, total: 0 });
  const [jsonDownloadError, setJsonDownloadError] = useState("");

  // Folder search
  const [folderSearch, setFolderSearch] = useState("");

  // Folder document search (within a folder) — server-side
  const [folderDocSearch, setFolderDocSearch] = useState("");
  const [folderDocStatusFilter, setFolderDocStatusFilter] = useState<"all" | DocStatus>("all");

  // All docs search
  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [displayedCount, setDisplayedCount] = useState(50);

  // Modals
  const [isBulkDownloadOpen, setIsBulkDownloadOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  // New dropdown
  const [isNewDropdownOpen, setIsNewDropdownOpen] = useState(false);
  const newDropdownRef = useRef<HTMLDivElement>(null);

  // Status dropdown per-row
  const [openStatusId, setOpenStatusId] = useState<string | null>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (newDropdownRef.current && !newDropdownRef.current.contains(e.target as Node)) {
        setIsNewDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setOpenStatusId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const loadFolders = useCallback(async (): Promise<AdminFolder[]> => {
    setFoldersLoading(true);
    setFoldersError("");
    try {
      const res = await adminGetFolders();
      const list = res.data ?? [];
      setFolders(list);
      return list;
    } catch {
      setFoldersError("Failed to load folders.");
      return [];
    } finally {
      setFoldersLoading(false);
    }
  }, []);

  // Fetches a window [0, limit) of documents for a folder, applying server-side
  // search + status filter. `mode` controls the loading indicator:
  //   - "initial": full table spinner (folder open, filter change)
  //   - "more":    "Show more" button spinner; appends are not used — we re-fetch
  //                the wider window and replace, which keeps polling logic simple
  //   - "silent":  polling refresh — no spinner
  const loadFolderDocs = useCallback(async (
    folder: AdminFolder,
    opts: { limit: number; search: string; status: "all" | DocStatus; mode: "initial" | "more" | "silent" },
  ): Promise<FolderDocument[]> => {
    if (opts.mode === "initial") setFolderDocsLoading(true);
    if (opts.mode === "more") setFolderDocsLoadingMore(true);
    try {
      const fetchOpts: { limit: number; offset: number; search?: string; status?: DocStatus } = {
        limit: opts.limit,
        offset: 0,
      };
      const trimmed = opts.search.trim();
      if (trimmed) fetchOpts.search = trimmed;
      if (opts.status !== "all") fetchOpts.status = opts.status;
      const res = await adminGetFolderDocuments(folder.id, fetchOpts);
      const docs: FolderDocument[] = res.data.documents ?? [];
      setFolderDocs(docs);
      setFolderDocsTotal(res.data.total ?? docs.length);
      setFolderTotal(res.data.folder_total ?? docs.length);
      if (res.data.status_counts) setFolderStatusCounts(res.data.status_counts);
      return docs;
    } catch {
      setFolderDocs([]);
      return [];
    } finally {
      if (opts.mode === "initial") setFolderDocsLoading(false);
      if (opts.mode === "more") setFolderDocsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const list = await loadFolders();
      const folderId = searchParams.get("folderId");
      if (!folderId) return;
      const folder = list.find((f) => f.id === folderId);
      if (folder) {
        const statusParam = searchParams.get("status");
        const validStatuses: DocStatus[] = ["pending", "ready", "validated", "delivered"];
        const status: "all" | DocStatus =
          statusParam && validStatuses.includes(statusParam as DocStatus)
            ? (statusParam as DocStatus)
            : "all";
        const loadedParam = parseInt(searchParams.get("loaded") ?? "", 10);
        if (Number.isFinite(loadedParam) && loadedParam > PAGE_SIZE) {
          initialLoadCountRef.current = loadedParam;
        }
        setActiveFolder(folder);
        setView("folder-detail");
        setFolderDocStatusFilter(status);
      } else {
        setSearchParams({}, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync with the folder + status tab + loaded count while viewing a folder.
  useEffect(() => {
    if (view !== "folder-detail" || !activeFolder) return;
    const params: Record<string, string> = { folderId: activeFolder.id };
    if (folderDocStatusFilter !== "all") params.status = folderDocStatusFilter;
    if (loadedCount > PAGE_SIZE) params.loaded = String(loadedCount);
    setSearchParams(params, { replace: true });
  }, [view, activeFolder, folderDocStatusFilter, loadedCount, setSearchParams]);

  useEffect(() => { loadedCountRef.current = loadedCount; }, [loadedCount]);
  useEffect(() => { folderDocSearchRef.current = folderDocSearch; }, [folderDocSearch]);
  useEffect(() => { folderDocStatusFilterRef.current = folderDocStatusFilter; }, [folderDocStatusFilter]);

  // Fetch the folder's first page (or re-fetch when search/status changes).
  // Search is debounced; folder-open and status-filter changes fire immediately.
  useEffect(() => {
    if (!activeFolder) return;
    const initialLimit = initialLoadCountRef.current ?? PAGE_SIZE;
    initialLoadCountRef.current = null;
    setLoadedCount(initialLimit);
    setSelectedDocIds(new Set());
    const delay = folderDocSearch.trim() ? 250 : 0;
    const timer = setTimeout(() => {
      loadFolderDocs(activeFolder, {
        limit: initialLimit,
        search: folderDocSearch,
        status: folderDocStatusFilter,
        mode: "initial",
      });
    }, delay);
    return () => clearTimeout(timer);
  }, [activeFolder, folderDocSearch, folderDocStatusFilter, loadFolderDocs]);

  const handleShowMore = () => {
    if (!activeFolder) return;
    const next = loadedCount + PAGE_SIZE;
    setLoadedCount(next);
    loadFolderDocs(activeFolder, {
      limit: next,
      search: folderDocSearch,
      status: folderDocStatusFilter,
      mode: "more",
    });
  };

  const [justFinishedIds, setJustFinishedIds] = useState<Set<string>>(new Set());
  const prevProcessingIds = useRef<Set<string>>(new Set());
  const hasProcessingRef = useRef(false);
  const lastSnapshotRef = useRef("");
  const pollDelayRef = useRef(2000);

  const snapshotFor = (docs: FolderDocument[]) =>
    docs.map((d) => `${d.id}:${d.status}:${d.is_processing ? 1 : 0}`).join("|");

  // Detect "just finished" transitions for the green "Done" badge.
  useEffect(() => {
    const currentProcessingIds = new Set(folderDocs.filter((d) => d.is_processing).map((d) => d.id));
    const finishedNow = [...prevProcessingIds.current].filter((id) => !currentProcessingIds.has(id));
    prevProcessingIds.current = currentProcessingIds;
    hasProcessingRef.current = currentProcessingIds.size > 0;

    if (finishedNow.length > 0) {
      setJustFinishedIds((prev) => new Set([...prev, ...finishedNow]));
      finishedNow.forEach((id) => {
        setTimeout(() => {
          setJustFinishedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        }, 2000);
      });
    }
  }, [folderDocs]);

  // Exponential-backoff poll while a folder is open and something is processing.
  // Doubles up to 30s on unchanged responses; resets to 2s the moment a doc
  // status flips. Reset and backoff are both decided synchronously from the
  // freshly-fetched response, before the next tick is scheduled.
  useEffect(() => {
    if (!activeFolder) return;
    // Re-arm to 2s whenever a new folder is opened.
    pollDelayRef.current = 2000;
    lastSnapshotRef.current = "";

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const schedule = (delay: number) => {
      timeoutId = setTimeout(async () => {
        if (cancelled) return;
        if (hasProcessingRef.current) {
          const docs = await loadFolderDocs(activeFolder, {
            limit: loadedCountRef.current,
            search: folderDocSearchRef.current,
            status: folderDocStatusFilterRef.current,
            mode: "silent",
          });
          if (cancelled) return;
          const snapshot = snapshotFor(docs);
          if (snapshot !== lastSnapshotRef.current) {
            lastSnapshotRef.current = snapshot;
            pollDelayRef.current = 2000;
          } else {
            pollDelayRef.current = Math.min(pollDelayRef.current * 2, 30000);
          }
        }
        if (!cancelled) schedule(pollDelayRef.current);
      }, delay);
    };

    schedule(pollDelayRef.current);
    return () => { cancelled = true; if (timeoutId) clearTimeout(timeoutId); };
  }, [activeFolder, loadFolderDocs]);

  useEffect(() => {
    if (view !== "all-docs") return;
    if (!searchQuery.trim()) { setDocuments([]); return; }
    const timer = setTimeout(async () => {
      setDocsLoading(true);
      setDocsError("");
      try {
        const res = await getAllDocumentsAdmin(searchQuery);
        setDocuments(res.data ?? []);
      } catch {
        setDocsError("Failed to load documents.");
      } finally {
        setDocsLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, view]);

  const openFolder = (folder: AdminFolder) => {
    setActiveFolder(folder);
    setView("folder-detail");
    setFolderDocSearch("");
    setFolderDocStatusFilter("all");
    setJsonDownloadError("");
    // URL is synced by the effect that watches [view, activeFolder, status].
    // Fetch is handled by the effect that watches [activeFolder, search, status].
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreateLoading(true);
    setCreateError("");
    try {
      await adminCreateFolder(newFolderName.trim());
      setNewFolderName("");
      setIsCreating(false);
      await loadFolders();
    } catch {
      setCreateError("Failed to create folder.");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteFolder = async (folder: AdminFolder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Documents inside will become unassigned.`)) return;
    setDeletingFolderId(folder.id);
    try {
      await adminDeleteFolder(folder.id);
      await loadFolders();
    } catch {
      // ignore
    } finally {
      setDeletingFolderId(null);
    }
  };

  const handleStatusChange = async (docId: string, status: "pending" | "ready" | "validated") => {
    setUpdatingStatusId(docId);
    try {
      const prevStatus = folderDocs.find((d) => d.id === docId)?.status;
      await adminUpdateDocumentStatus(docId, status);
      setFolderDocs((prev) => prev.map((d) => d.id === docId ? { ...d, status } : d));
      // Keep folder-wide status counts (deliver button + filter chips) in sync.
      if (prevStatus && prevStatus !== status) {
        setFolderStatusCounts((prev) => ({
          ...prev,
          [prevStatus]: Math.max(0, prev[prevStatus] - 1),
          [status]: prev[status] + 1,
        }));
      }
    } catch {
      // ignore
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    setDeletingDocId(docId);
    try {
      const deleted = folderDocs.find((d) => d.id === docId);
      await deleteDocument(docId);
      setFolderDocs((prev) => prev.filter((d) => d.id !== docId));
      if (deleted) {
        setFolderStatusCounts((prev) => ({ ...prev, [deleted.status]: Math.max(0, prev[deleted.status] - 1) }));
        setFolderTotal((prev) => Math.max(0, prev - 1));
        setFolderDocsTotal((prev) => Math.max(0, prev - 1));
      }
      setSelectedDocIds((prev) => {
        if (!prev.has(docId)) return prev;
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
      if (activeFolder) {
        const wasPending = deleted && deleted.status !== "delivered";
        setActiveFolder((prev) => prev ? {
          ...prev,
          doc_count: prev.doc_count - 1,
          pending_count: wasPending ? prev.pending_count - 1 : prev.pending_count,
        } : prev);
      }
    } catch {
      // ignore
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocIds.size === 0 || !activeFolder) return;
    const count = selectedDocIds.size;
    if (!window.confirm(`Delete ${count} document${count !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    setIsBulkDeleting(true);
    try {
      const ids = Array.from(selectedDocIds);
      const docsBeingDeleted = folderDocs.filter((d) => selectedDocIds.has(d.id));
      await Promise.all(ids.map((id) => deleteDocument(id)));
      setFolderDocs((prev) => prev.filter((d) => !selectedDocIds.has(d.id)));
      // Keep folder-wide status counts + totals in sync with the removed docs.
      setFolderStatusCounts((prev) => {
        const next = { ...prev };
        docsBeingDeleted.forEach((d) => { next[d.status] = Math.max(0, next[d.status] - 1); });
        return next;
      });
      setFolderTotal((prev) => Math.max(0, prev - docsBeingDeleted.length));
      setFolderDocsTotal((prev) => Math.max(0, prev - docsBeingDeleted.length));
      const pendingDeleted = docsBeingDeleted.filter((d) => d.status !== "delivered").length;
      setActiveFolder((prev) => prev ? {
        ...prev,
        doc_count: prev.doc_count - docsBeingDeleted.length,
        pending_count: prev.pending_count - pendingDeleted,
      } : prev);
      setSelectedDocIds(new Set());
    } catch {
      // ignore
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Download the raw JSON (same content as each document's "Raw JSON" tab) for
  // every document in the folder, bundled into a single ZIP. Fetches the full
  // folder document list (not just the loaded window) and exports them one at a
  // time so progress can be shown; individual failures are skipped.
  const handleDownloadFolderJson = async () => {
    if (!activeFolder || isDownloadingJson) return;
    setIsDownloadingJson(true);
    setJsonDownloadError("");
    setJsonDownloadProgress({ done: 0, total: 0 });

    const sanitize = (s: string, fallback: string) =>
      (s || "").replace(/[^a-z0-9]/gi, "_").replace(/_+/g, "_").replace(/^_|_$/g, "") || fallback;

    try {
      const res = await adminGetFolderDocuments(activeFolder.id, { limit: 10000 });
      const docs: FolderDocument[] = (res.data.documents ?? []).filter(
        (d: FolderDocument) => d.page_count > 0,
      );
      if (docs.length === 0) {
        setJsonDownloadError("This folder has no documents with data to download.");
        return;
      }
      setJsonDownloadProgress({ done: 0, total: docs.length });

      const entries: { name: string; data: string }[] = [];
      const usedNames = new Set<string>();
      let failed = 0;

      for (const doc of docs) {
        try {
          const json = await fetchDocumentRawJsonString(doc.id, doc.name, doc.page_count);
          const base = sanitize(doc.name, "document");
          let name = `${base}.json`;
          let n = 2;
          while (usedNames.has(name.toLowerCase())) name = `${base}_${n++}.json`;
          usedNames.add(name.toLowerCase());
          entries.push({ name, data: json });
        } catch {
          failed++;
        } finally {
          setJsonDownloadProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }

      if (entries.length === 0) {
        setJsonDownloadError("Failed to fetch document data. Please try again.");
        return;
      }

      const blob = await createZipBlob(entries);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sanitize(activeFolder.name, "folder")}_json.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (failed > 0) {
        setJsonDownloadError(
          `Downloaded ${entries.length} file${entries.length !== 1 ? "s" : ""}; ${failed} document${failed !== 1 ? "s" : ""} could not be exported.`,
        );
      }
    } catch {
      setJsonDownloadError("Failed to download JSON files. Please try again.");
    } finally {
      setIsDownloadingJson(false);
    }
  };

  const selectableFolderDocs = folderDocs.filter((d) => !d.is_processing && d.status !== "delivered");
  const allSelectableSelected =
    selectableFolderDocs.length > 0 &&
    selectableFolderDocs.every((d) => selectedDocIds.has(d.id));

  const handleSelectAllDocs = (checked: boolean) => {
    if (checked) {
      setSelectedDocIds(new Set(selectableFolderDocs.map((d) => d.id)));
    } else {
      setSelectedDocIds(new Set());
    }
  };

  const handleSelectDoc = (docId: string, checked: boolean) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(docId);
      else next.delete(docId);
      return next;
    });
  };

  const handleAssigned = async () => {
    // Refresh folder docs and folder list after ownership assignment
    if (activeFolder) {
      await loadFolderDocs(activeFolder, {
        limit: loadedCount,
        search: folderDocSearch,
        status: folderDocStatusFilter,
        mode: "silent",
      });
      await loadFolders();
      setActiveFolder((prev) => prev ? { ...prev, pending_count: 0 } : prev);
    }
    setIsAssignOpen(false);
  };

  const filteredFolders = folders.filter((f) =>
    f.name.toLowerCase().includes(folderSearch.toLowerCase()),
  );

  const sortedDocuments = [...documents].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });
  const displayedDocuments = sortedDocuments.slice(0, displayedCount);
  const remainingCount = sortedDocuments.length - displayedCount;

  const folderChipCounts: Record<"all" | DocStatus, number> = {
    all: folderTotal,
    pending: folderStatusCounts.pending,
    ready: folderStatusCounts.ready,
    validated: folderStatusCounts.validated,
    delivered: folderStatusCounts.delivered,
  };
  const hasMoreFolderDocs = folderDocs.length < folderDocsTotal;

  // ── Folder detail view ──────────────────────────────────────────────────────
  if (view === "folder-detail" && activeFolder) {
    return (
      <div className="bg-white h-full flex flex-col overflow-hidden">
        <div className="w-full mx-auto pt-6 sm:pt-10 px-4 sm:px-6 lg:px-10 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <button
                onClick={() => { setView("folders"); setSearchParams({}); loadFolders(); }}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-2 transition-colors"
              >
                <ArrowLeft size={14} />
                Back to folders
              </button>
              <h3 className="text-xl font-semibold tracking-tight text-gray-900 flex items-center gap-2">
                <Folder size={18} className="text-[#1d365d]" />
                {activeFolder.name}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {activeFolder.doc_count} document{activeFolder.doc_count !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {selectedDocIds.size > 0 && (
                <button
                  onClick={handleBulkDelete}
                  disabled={isBulkDeleting}
                  className="flex items-center gap-2 h-9 px-4 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isBulkDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Delete {selectedDocIds.size}
                </button>
              )}
              <button
                onClick={() => setIsUploadOpen(true)}
                className="flex items-center gap-2 h-9 px-4 text-sm font-medium text-white bg-[#1d365d] hover:bg-[#2d4a75] rounded-lg transition-colors"
              >
                <Upload size={14} />
                Upload to folder
              </button>
              {(() => {
                const readyCount = folderStatusCounts.ready;
                return (
                  <button
                    onClick={() => setIsAssignOpen(true)}
                    disabled={readyCount === 0}
                    className="flex items-center gap-2 h-9 px-4 text-sm font-medium text-[#1d365d] border border-[#1d365d]/30 rounded-lg hover:bg-[#1d365d]/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <UserCheck size={14} />
                    Deliver {readyCount > 0 ? `${readyCount} ` : ""}document{readyCount !== 1 ? "s" : ""}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pb-10">
          {jsonDownloadError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">{jsonDownloadError}</p>
          )}
          {(folderTotal > 0 || folderDocSearch.trim() !== "" || folderDocStatusFilter !== "all") && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <div className="relative flex-shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  className="w-full sm:w-72 h-9 pl-9 pr-4 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
                  placeholder="Search documents..."
                  value={folderDocSearch}
                  onChange={(e) => setFolderDocSearch(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-1 p-0.5 bg-gray-50 border border-gray-200 rounded-lg w-fit">
                {(["all", "pending", "ready", "validated", "delivered"] as const).map((s) => {
                  const isActive = folderDocStatusFilter === s;
                  const label = s === "all" ? "All" : s === "pending" ? "Pending" : s === "ready" ? "Ready" : s === "validated" ? "Action required" : "Delivered";
                  return (
                    <button
                      key={s}
                      onClick={() => setFolderDocStatusFilter(s)}
                      className={`flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md transition-colors ${
                        isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800"
                      }`}
                    >
                      {s !== "all" && (
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          s === "ready" ? "bg-green-500" : s === "validated" ? "bg-violet-500" : s === "pending" ? "bg-amber-400" : "bg-blue-400"
                        }`} />
                      )}
                      {label}
                      <span className={`text-[10px] ${isActive ? "text-gray-400" : "text-gray-400"}`}>
                        {folderChipCounts[s]}
                      </span>
                    </button>
                  );
                })}
              </div>
              {folderTotal > 0 && (
                <button
                  onClick={handleDownloadFolderJson}
                  disabled={isDownloadingJson}
                  className="flex items-center justify-center gap-2 h-9 px-4 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 sm:ml-auto"
                >
                  {isDownloadingJson ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {isDownloadingJson
                    ? jsonDownloadProgress.total > 0
                      ? `Downloading ${jsonDownloadProgress.done}/${jsonDownloadProgress.total}…`
                      : "Preparing…"
                    : "Download JSON"}
                </button>
              )}
            </div>
          )}

          <div className="border border-gray-200 rounded-lg overflow-visible">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-3 py-3 text-left w-10 rounded-tl-lg">
                    <input
                      type="checkbox"
                      checked={allSelectableSelected}
                      onChange={(e) => handleSelectAllDocs(e.target.checked)}
                      disabled={selectableFolderDocs.length === 0}
                      className="w-3.5 h-3.5 cursor-pointer accent-[#1d365d] disabled:cursor-not-allowed"
                      aria-label="Select all documents"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden sm:table-cell">Pages</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden md:table-cell">Uploaded</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden lg:table-cell">Marked by</th>
                  <th className="px-4 py-3 rounded-tr-lg" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {folderDocsLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <Loader2 className="w-6 h-6 mx-auto text-gray-300 animate-spin" />
                    </td>
                  </tr>
                ) : folderTotal === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <FileText className="w-8 h-8 mx-auto mb-3 text-gray-200" />
                      <p className="text-sm font-medium text-gray-900 mb-1">No documents yet</p>
                      <p className="text-sm text-gray-400">Upload documents to this folder using the button above.</p>
                    </td>
                  </tr>
                ) : folderDocs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <Search className="w-8 h-8 mx-auto mb-3 text-gray-200" />
                      <p className="text-sm font-medium text-gray-900 mb-1">No documents match</p>
                      <p className="text-sm text-gray-400">Try a different search term or status filter.</p>
                    </td>
                  </tr>
                ) : (
                  folderDocs.map((doc) => {
                    const selectable = !doc.is_processing && doc.status !== "delivered";
                    return (
                    <tr
                      key={doc.id}
                      className={`hover:bg-gray-50 transition-colors ${doc.is_processing ? "cursor-default" : "cursor-pointer"} ${doc.status === "delivered" ? "opacity-60" : ""}`}
                      onClick={() => { if (!doc.is_processing) navigate(`/view/${doc.id}`); }}
                    >
                      <td className="px-3 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                        {selectable && (
                          <input
                            type="checkbox"
                            checked={selectedDocIds.has(doc.id)}
                            onChange={(e) => handleSelectDoc(doc.id, e.target.checked)}
                            className="w-3.5 h-3.5 cursor-pointer accent-[#1d365d]"
                            aria-label={`Select ${doc.name}`}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 h-7 w-7 bg-gray-100 rounded-full flex items-center justify-center">
                            <FileText className="w-3.5 h-3.5 text-gray-500" />
                          </div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[240px] sm:max-w-[320px]">{doc.name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">{doc.page_count}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
                        {doc.created_at
                          ? new Date(doc.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                        {doc.is_processing ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2.5 py-0.5">
                            <Loader2 size={10} className="animate-spin" />
                            {typeof doc.pending_page_count === "number" && doc.page_count > 0
                              ? `Processing ${doc.page_count - doc.pending_page_count}/${doc.page_count}`
                              : "Processing…"}
                          </span>
                        ) : justFinishedIds.has(doc.id) ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
                            <CheckCircle size={10} />
                            Done
                          </span>
                        ) : doc.status === "delivered" ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                            Delivered{doc.delivered_to ? ` · ${doc.delivered_to}` : ""}
                          </span>
                        ) : (
                          <div className="relative inline-block" ref={openStatusId === doc.id ? statusDropdownRef : null}>
                            <button
                              disabled={updatingStatusId === doc.id}
                              onClick={() => setOpenStatusId((prev) => prev === doc.id ? null : doc.id)}
                              className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-0.5 border transition-colors ${
                                doc.status === "ready"
                                  ? "text-green-700 bg-green-50 border-green-200 hover:bg-green-100"
                                  : doc.status === "validated"
                                  ? "text-violet-700 bg-violet-50 border-violet-200 hover:bg-violet-100"
                                  : "text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100"
                              }`}
                            >
                              {updatingStatusId === doc.id
                                ? <Loader2 size={10} className="animate-spin" />
                                : <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${doc.status === "ready" ? "bg-green-500" : doc.status === "validated" ? "bg-violet-500" : "bg-amber-400"}`} />
                              }
                              {doc.status === "ready" ? "Ready" : doc.status === "validated" ? "Action required" : "Pending"}
                              <ChevronDown size={10} className={`transition-transform ${openStatusId === doc.id ? "rotate-180" : ""}`} />
                            </button>
                            {openStatusId === doc.id && (
                              <div className="absolute left-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-30 overflow-hidden">
                                {(["pending", "ready", "validated"] as const).map((s) => (
                                  <button
                                    key={s}
                                    onClick={() => { setOpenStatusId(null); if (s !== doc.status) handleStatusChange(doc.id, s); }}
                                    className={`flex items-center gap-2 w-full px-3 py-2 text-xs font-medium transition-colors hover:bg-gray-50 ${s === doc.status ? "opacity-40 cursor-default" : ""}`}
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s === "ready" ? "bg-green-500" : s === "validated" ? "bg-violet-500" : "bg-amber-400"}`} />
                                    <span className={s === "ready" ? "text-green-700" : s === "validated" ? "text-violet-700" : "text-amber-700"}>
                                      {s === "ready" ? "Ready" : s === "validated" ? "Action required" : "Pending"}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                        {doc.marked_by ? (
                          <span className="truncate inline-block max-w-[180px] align-middle">{doc.marked_by}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!doc.is_processing && doc.status !== "delivered" && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteDoc(doc.id); }}
                            disabled={deletingDocId === doc.id}
                            className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                          >
                            {deletingDocId === doc.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            {folderDocs.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-400">
                  {folderDocSearch.trim() || folderDocStatusFilter !== "all" ? (
                    <>Showing <span className="font-medium text-gray-600">{folderDocs.length}</span> of <span className="font-medium text-gray-600">{folderDocsTotal}</span></>
                  ) : (
                    <>Showing <span className="font-medium text-gray-600">{folderDocs.length}</span> of <span className="font-medium text-gray-600">{folderTotal}</span></>
                  )}
                </p>
                {hasMoreFolderDocs && (
                  <button
                    onClick={handleShowMore}
                    disabled={folderDocsLoadingMore}
                    className="flex items-center gap-1.5 text-xs font-medium text-[#1d365d] hover:text-[#2d4a75] transition-colors disabled:opacity-50"
                  >
                    {folderDocsLoadingMore && <Loader2 size={12} className="animate-spin" />}
                    Show {Math.min(PAGE_SIZE, folderDocsTotal - folderDocs.length)} more
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <AdminUploadProcessedModal
          isOpen={isUploadOpen}
          setIsOpen={(open) => {
            setIsUploadOpen(open);
            if (!open) loadFolderDocs(activeFolder, {
              limit: loadedCount,
              search: folderDocSearch,
              status: folderDocStatusFilter,
              mode: "silent",
            });
          }}
          preselectedFolderId={activeFolder.id}
        />
        <AdminFolderAssignModal
          isOpen={isAssignOpen}
          setIsOpen={setIsAssignOpen}
          folderId={activeFolder.id}
          folderName={activeFolder.name}
          docCount={folderStatusCounts.ready}
          onAssigned={handleAssigned}
        />
      </div>
    );
  }

  // ── All docs search view ────────────────────────────────────────────────────
  if (view === "all-docs") {
    return (
      <div className="bg-white h-full flex flex-col overflow-hidden">
        <div className="w-full mx-auto pt-6 sm:pt-10 px-4 sm:px-6 lg:px-10 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <button
                onClick={() => setView("folders")}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-2 transition-colors"
              >
                <ArrowLeft size={14} />
                Back to folders
              </button>
              <h3 className="text-xl font-semibold tracking-tight text-gray-900">All documents</h3>
              <p className="text-sm text-gray-500 mt-0.5">Search across all users and organisations</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setIsBulkDownloadOpen(true)}
                className="flex items-center gap-2 h-9 px-4 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download size={14} />
                Download bulk files
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pb-10">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              className="w-full sm:max-w-xs h-9 pl-9 pr-4 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {docsError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">{docsError}</p>
          )}

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden sm:table-cell">Owner</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden md:table-cell">Organisation</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden sm:table-cell">Pages</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden md:table-cell">
                    <button
                      onClick={() => setSortOrder((p) => p === "asc" ? "desc" : "asc")}
                      className="flex items-center gap-1.5 uppercase tracking-widest hover:text-gray-600 transition-colors"
                      disabled={documents.length === 0}
                    >
                      Created
                      {sortOrder === "desc" ? <ArrowDown size={13} /> : <ArrowUp size={13} />}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {docsLoading ? (
                  <tr><td colSpan={5} className="px-4 py-16 text-center"><p className="text-sm text-gray-400">Loading…</p></td></tr>
                ) : documents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <FileText className="w-8 h-8 mx-auto mb-3 text-gray-200" />
                      <p className="text-sm font-medium text-gray-900 mb-1">{searchQuery ? "No documents found" : "Search for documents above"}</p>
                      <p className="text-sm text-gray-400">{searchQuery ? `No documents match "${searchQuery}"` : ""}</p>
                    </td>
                  </tr>
                ) : (
                  displayedDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 h-7 w-7 bg-gray-100 rounded-full flex items-center justify-center">
                            <FileText className="w-3.5 h-3.5 text-gray-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate max-w-[200px] sm:max-w-[280px]">{doc.name}</p>
                            <p className="text-xs text-gray-400 sm:hidden mt-0.5">{doc.user_email ?? "—"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <User size={13} className="text-gray-400 flex-shrink-0" />
                          <span className="truncate max-w-[160px]">{doc.user_name || doc.user_email || "—"}</span>
                        </div>
                        {doc.user_name && doc.user_email && (
                          <p className="text-xs text-gray-400 mt-0.5 ml-5 truncate max-w-[160px]">{doc.user_email}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {doc.organisation_name ? (
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <Building2 size={13} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate max-w-[140px]">{doc.organisation_name}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Personal</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">{doc.page_count}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
                        {doc.created_at ? new Date(doc.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {!docsLoading && documents.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400">Total: <span className="font-medium text-gray-600">{documents.length}</span></p>
              </div>
            )}
          </div>

          {remainingCount > 0 && (
            <div className="mt-4 text-center">
              <button onClick={() => setDisplayedCount((p) => p + 50)} className="text-sm text-[#1d365d] hover:text-[#2d4a75] font-medium transition-colors">
                Show {Math.min(remainingCount, 50)} more
              </button>
              <p className="text-xs text-gray-400 mt-1">
                Showing {displayedCount} of {documents.length}.{" "}
                <button onClick={() => setDisplayedCount(documents.length)} className="text-[#1d365d] hover:text-[#2d4a75] font-medium transition-colors">Show all</button>
              </p>
            </div>
          )}
        </div>

        <BulkDownloadModal isOpen={isBulkDownloadOpen} setIsOpen={setIsBulkDownloadOpen} />
      </div>
    );
  }

  // ── Folders list (default) ──────────────────────────────────────────────────
  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-6 sm:pt-10 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">Documents</h3>
            <p className="text-sm text-gray-500 mt-0.5">Create staging folders, upload documents, then assign ownership</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="relative" ref={newDropdownRef}>
              <button
                onClick={() => setIsNewDropdownOpen((o) => !o)}
                className="flex items-center gap-2 h-9 px-4 text-sm font-medium text-white bg-[#1d365d] hover:bg-[#2d4a75] rounded-lg transition-colors"
              >
                New
                <ChevronDown size={14} className={`transition-transform ${isNewDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {isNewDropdownOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                  <button
                    onClick={() => { setIsNewDropdownOpen(false); setIsCreating(true); }}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Folder size={14} className="text-gray-400" />
                    Folder
                  </button>
                  <button
                    onClick={() => { setIsNewDropdownOpen(false); setIsBulkDownloadOpen(true); }}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Download size={14} className="text-gray-400" />
                    Bulk download
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create folder form */}
        {isCreating && (
          <div className="mb-6 flex items-center gap-2">
            <input
              type="text"
              autoFocus
              placeholder="Folder name…"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCreateFolder(); if (e.key === "Escape") { setIsCreating(false); setNewFolderName(""); setCreateError(""); } }}
              className="h-9 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors w-64"
            />
            <button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || createLoading}
              className="h-9 px-4 text-sm font-medium text-white bg-[#1d365d] hover:bg-[#2d4a75] rounded-lg transition-colors disabled:opacity-50"
            >
              {createLoading ? <Loader2 size={14} className="animate-spin" /> : "Create"}
            </button>
            <button
              onClick={() => { setIsCreating(false); setNewFolderName(""); setCreateError(""); }}
              className="h-9 w-9 flex items-center justify-center text-gray-400 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <X size={14} />
            </button>
            {createError && <p className="text-xs text-red-600">{createError}</p>}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pb-10">
        {foldersError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">{foldersError}</p>
        )}

        {!foldersLoading && folders.length > 0 && (
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              className="w-full sm:max-w-xs h-9 pl-9 pr-4 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
              placeholder="Search folders..."
              value={folderSearch}
              onChange={(e) => setFolderSearch(e.target.value)}
            />
          </div>
        )}

        {foldersLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-gray-300 animate-spin" />
          </div>
        ) : folders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Folder className="w-12 h-12 text-gray-200 mb-4" />
            <p className="text-sm font-medium text-gray-900 mb-1">No staging folders yet</p>
            <p className="text-sm text-gray-400 mb-4">Create a folder to start staging documents before assigning them to users or organisations.</p>
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 h-9 px-4 text-sm font-medium text-white bg-[#1d365d] hover:bg-[#2d4a75] rounded-lg transition-colors"
            >
              <Plus size={14} />
              Create first folder
            </button>
          </div>
        ) : filteredFolders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Search className="w-8 h-8 text-gray-200 mb-3" />
            <p className="text-sm font-medium text-gray-900 mb-1">No folders match</p>
            <p className="text-sm text-gray-400">Try a different search term.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredFolders.map((folder) => (
              <div
                key={folder.id}
                className="group relative border border-gray-200 rounded-xl p-4 hover:border-[#1d365d]/30 hover:bg-[#1d365d]/[0.02] transition-all cursor-pointer"
                onClick={() => openFolder(folder)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="flex-shrink-0 w-9 h-9 bg-[#1d365d]/8 rounded-lg flex items-center justify-center">
                      <Folder size={18} className="text-[#1d365d]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{folder.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {folder.doc_count} document{folder.doc_count !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                    disabled={deletingFolderId === folder.id}
                    className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                  >
                    {deletingFolderId === folder.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </button>
                </div>

                {folder.pending_count > 0 ? (
                  <div className="mt-3 flex items-center gap-1.5">
                    <div className="flex-1 h-1 bg-amber-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full w-full" />
                    </div>
                    <span className="text-[10px] font-medium text-amber-600 flex-shrink-0">
                      {folder.pending_count} pending delivery
                    </span>
                  </div>
                ) : folder.doc_count > 0 ? (
                  <div className="mt-3 flex items-center gap-1.5">
                    <div className="flex-1 h-1 bg-green-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-400 rounded-full w-full" />
                    </div>
                    <span className="text-[10px] font-medium text-green-600 flex-shrink-0">All delivered</span>
                  </div>
                ) : null}

                {folder.created_at && (
                  <p className="text-[10px] text-gray-300 mt-2">
                    {new Date(folder.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <BulkDownloadModal isOpen={isBulkDownloadOpen} setIsOpen={setIsBulkDownloadOpen} />
      <AdminUploadProcessedModal isOpen={isUploadOpen} setIsOpen={(open) => { setIsUploadOpen(open); if (!open) loadFolders(); }} />
    </div>
  );
}

export default AdminDocuments;
